/**
 * Modul „CRM“ (#830) — Feldkatalog der Masken (alle Person-Felder aus der Feldliste #830 in Gruppen), Rechte-Logik,
 * deutsche Beschriftungen der CHECK-Werte (API-Werte bleiben unverändert) und reine Umrechnungen
 * Anzeige ↔ Formular ↔ API. Ohne React, damit es per Vitest prüfbar ist.
 * Zeitfelder: Formular in Europe/Berlin (datetime-local), API in UTC — beide Richtungen dieselbe Zone; nur tatsächlich
 * im Formular geänderte Felder gehen in den PATCH (Vergleich auf Formularebene), unberührte Sekunden bleiben erhalten.
 */
import {
  ADRESSE_TYPEN, EINWILLIGUNG_KANAELE, EINWILLIGUNG_STATUS, KONTAKT_KANAELE, KONTOSTATUS_SHOPIFY, STATUS_NEO,
  type CrmFiliale, type CrmPerson, type Me, type ModulFreischaltung,
} from '@portal/contracts';

/** `bool3` = ja/nein/— (nullable), `ro-tech` = technische Kennung, nur lesen, dezent */
export type FeldArt = 'text' | 'textarea' | 'bool' | 'bool3' | 'int' | 'dezimal' | 'datum' | 'zeit' | 'select' | 'tags' | 'ro' | 'ro-zeit' | 'ro-tech';
export interface FeldDef {
  key: string;
  label: string;
  art: FeldArt;
  optionen?: readonly string[];
  /** #851 Beschriftung je Option (z. B. Filialname), sonst WERT_LABELS bzw. Rohwert */
  optionLabels?: Readonly<Record<string, string>>;
  /** #851 Eingabe vorübergehend gesperrt (z. B. Auswahl lädt noch / Laden fehlgeschlagen) — Wert bleibt erhalten */
  gesperrt?: boolean;
  /** #851 Statuszeile am Feld (Laden/Fehler), optional mit „Erneut laden“ */
  status?: { text: string; fehler?: boolean; erneut?: () => void };
  /** Herkunft (Shopify / NEO / Loyalty / Portal) als dezenter Hinweis */
  herkunft?: string;
}
export interface Gruppe {
  titel: string;
  felder: FeldDef[];
}

export const CRM_PFAD = '/crm';
export const PROTOKOLL_PFAD = '/crm/protokoll';
export const kundenPfad = (id: string) => `${CRM_PFAD}/kunden/${encodeURIComponent(id)}`;

/** Unterseite aus dem Pfad: Liste, Protokoll oder Datensatz (id) */
export function crmRoute(pathname: string): { seite: 'liste' } | { seite: 'protokoll' } | { seite: 'kunde'; id: string } | { seite: 'unbekannt' } {
  if (pathname === CRM_PFAD || pathname === `${CRM_PFAD}/`) return { seite: 'liste' };
  if (pathname === PROTOKOLL_PFAD) return { seite: 'protokoll' };
  const m = /^\/crm\/kunden\/([^/]+)\/?$/.exec(pathname);
  if (m) return { seite: 'kunde', id: decodeURIComponent(m[1]!) };
  return { seite: 'unbekannt' };
}

/** Schreibrechte nur ab Stufe bearbeiten (Freischaltung mod-crm-*, /api/me.modules.crm); lesen zeigt keine Bearbeiten-Elemente */
export function darfBearbeiten(me: Pick<Me, 'modules'> | { modules?: ModulFreischaltung }): boolean {
  const s = me.modules?.crm;
  return s === 'bearbeiten' || s === 'verwalten';
}

/* ---------------------------------------------------------- Beschriftungen der CHECK-Werte ---------------------------------------------------------- */

/** Deutsche Beschriftung je API-Wert (value/gesendeter Code bleibt der Rohwert; unbekannte Werte erscheinen roh) */
export const WERT_LABELS: Record<string, Record<string, string>> = {
  kontostatusShopify: { ENABLED: 'Aktiv', INVITED: 'Eingeladen', DISABLED: 'Deaktiviert', DECLINED: 'Abgelehnt' },
  statusNeo: { aktiv: 'Aktiv', gesperrt: 'Gesperrt' },
  typ: { rechnung: 'Rechnungsadresse', liefer: 'Lieferadresse', standard: 'Standardadresse' },
  kanal: { email: 'E-Mail', telefon: 'Telefon', mobil: 'Mobil', fax: 'Fax', sms: 'SMS', post: 'Post', whatsapp: 'WhatsApp' },
  status: { keine: 'Keine', einfach: 'Einfach (Single-Opt-in)', bestaetigt: 'Bestätigt (Double-Opt-in)', widerrufen: 'Widerrufen' },
  quelle: { shopify: 'Shop', neo: 'NEO', portal: 'Portal' },
  system: { shopify: 'Shop', neo: 'NEO', loyalty: 'Loyalty', n8n_link: 'n8n-Link', brevo: 'Brevo', zoho: 'Zoho', portal: 'Portal' },
};
/** Spaltenname aus dem Protokoll (snake_case) → Feldschlüssel der Maske */
export const zuFeldKey = (feld: string): string => feld.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());

export function wertLabel(feldKey: string, roh: unknown): string {
  if (typeof roh !== 'string') return wertText(roh);
  return WERT_LABELS[zuFeldKey(feldKey)]?.[roh] ?? roh;
}

export const PERSON_GRUPPEN: Gruppe[] = [
  {
    titel: 'Person',
    felder: [
      { key: 'anrede', label: 'Anrede', art: 'text', herkunft: 'Shopify/NEO' },
      { key: 'titel', label: 'Titel', art: 'text', herkunft: 'NEO' },
      { key: 'vorname', label: 'Vorname', art: 'text', herkunft: 'Shopify/NEO' },
      { key: 'nachname', label: 'Nachname', art: 'text', herkunft: 'Shopify/NEO' },
      { key: 'name', label: 'Anzeigename', art: 'text', herkunft: 'Portal (Altfeld)' },
      { key: 'firma', label: 'Firma', art: 'text', herkunft: 'Shopify/NEO' },
      { key: 'geschlecht', label: 'Geschlecht', art: 'text', herkunft: 'NEO' },
      { key: 'geburtstag', label: 'Geburtstag', art: 'datum', herkunft: 'NEO/Loyalty' },
      { key: 'sprache', label: 'Sprache', art: 'text', herkunft: 'Shopify' },
      { key: 'kundentyp', label: 'Kundentyp', art: 'text', herkunft: 'NEO' },
      { key: 'stammfiliale', label: 'Stammfiliale', art: 'text', herkunft: 'NEO' },
      { key: 'kundengruppe', label: 'Kundengruppe', art: 'text', herkunft: 'NEO' },
    ],
  },
  {
    titel: 'Status, Sperre, Blacklist',
    felder: [
      { key: 'kontostatusShopify', label: 'Kontostatus Shop', art: 'select', optionen: KONTOSTATUS_SHOPIFY, herkunft: 'Shopify' },
      { key: 'statusNeo', label: 'Status Warenwirtschaft', art: 'select', optionen: STATUS_NEO, herkunft: 'NEO' },
      { key: 'gesperrtGrund', label: 'Sperrgrund', art: 'textarea', herkunft: 'NEO' },
      { key: 'blacklist', label: 'Blacklist', art: 'bool', herkunft: 'Portal' },
      { key: 'blacklistGrund', label: 'Blacklist-Grund', art: 'textarea', herkunft: 'Portal' },
      { key: 'blacklistQuelle', label: 'Blacklist-Quelle', art: 'text', herkunft: 'Portal' },
      { key: 'blacklistAufgehobenAm', label: 'Blacklist aufgehoben am', art: 'zeit', herkunft: 'Portal' },
      { key: 'blacklistAufgehobenVon', label: 'Blacklist aufgehoben von', art: 'text', herkunft: 'Portal' },
      { key: 'deaktiviertAt', label: 'Deaktiviert am', art: 'ro-zeit', herkunft: 'Portal' },
    ],
  },
  {
    titel: 'Kauf und Loyalty',
    felder: [
      { key: 'erstkauf', label: 'Erstkauf', art: 'zeit', herkunft: 'Shopify/NEO' },
      { key: 'letztkauf', label: 'Letztkauf', art: 'zeit', herkunft: 'jüngster Wert aller Quellen' },
      { key: 'bestellanzahl', label: 'Bestellungen', art: 'int', herkunft: 'Shopify' },
      { key: 'umsatzGesamt', label: 'Umsatz gesamt (€)', art: 'dezimal', herkunft: 'Summe aller Quellen' },
      { key: 'bonussaldo', label: 'Bonussaldo', art: 'dezimal', herkunft: 'Loyalty' },
      { key: 'bonuskarteNr', label: 'Bonuskarten-Nr.', art: 'text', herkunft: 'Loyalty' },
      { key: 'loyaltyStatus', label: 'Loyalty-Status', art: 'text', herkunft: 'Loyalty' },
    ],
  },
  {
    titel: 'Notizen und Tags',
    felder: [
      { key: 'notiz', label: 'Notiz', art: 'textarea', herkunft: 'Portal' },
      { key: 'kassentext', label: 'Kassentext', art: 'textarea', herkunft: 'NEO' },
      { key: 'tags', label: 'Tags', art: 'tags', herkunft: 'Shopify' },
      { key: 'dubletteVon', label: 'Dublette von (Kunden-ID)', art: 'text', herkunft: 'Portal' },
    ],
  },
  {
    titel: 'Quellen',
    felder: [
      { key: 'quelle', label: 'Quelle', art: 'ro', herkunft: 'Portal' },
      { key: 'quellenId', label: 'Quellen-ID', art: 'ro', herkunft: 'Quellsystem' },
      { key: 'email', label: 'E-Mail (Altfeld)', art: 'ro', herkunft: 'Shopify/NEO' },
      { key: 'telefon', label: 'Telefon (Altfeld)', art: 'ro', herkunft: 'Shopify/NEO' },
      { key: 'id', label: 'Kunden-ID (Portal)', art: 'ro-tech', herkunft: 'Portal' },
    ],
  },
  {
    titel: 'Portal-Zeitstempel',
    felder: [
      { key: 'erstelltAmQuelle', label: 'Angelegt im Quellsystem', art: 'zeit', herkunft: 'Shopify' },
      { key: 'erstelltAt', label: 'Angelegt im Portal', art: 'ro-zeit', herkunft: 'Portal' },
      { key: 'geaendertAt', label: 'Zuletzt geändert', art: 'ro-zeit', herkunft: 'Portal' },
    ],
  },
];

export const ADRESSE_FELDER: FeldDef[] = [
  { key: 'typ', label: 'Typ', art: 'select', optionen: ADRESSE_TYPEN },
  { key: 'firma', label: 'Firma', art: 'text' },
  { key: 'strasse', label: 'Straße', art: 'text' },
  { key: 'hausnummer', label: 'Hausnummer', art: 'text' },
  { key: 'zusatz', label: 'Zusatz', art: 'text' },
  { key: 'plz', label: 'PLZ', art: 'text' },
  { key: 'ort', label: 'Ort', art: 'text' },
  { key: 'landRoh', label: 'Land (wie geliefert)', art: 'text' },
  { key: 'landIso2', label: 'Land (ISO-2)', art: 'text' },
  { key: 'istStandard', label: 'Standardadresse', art: 'bool' },
  { key: 'quelle', label: 'Quelle', art: 'text' },
  { key: 'gueltigAb', label: 'Gültig ab', art: 'zeit' },
  { key: 'gueltigBis', label: 'Gültig bis', art: 'zeit' },
  { key: 'erstelltAt', label: 'Angelegt', art: 'ro-zeit' },
  { key: 'geaendertAt', label: 'Geändert', art: 'ro-zeit' },
  { key: 'id', label: 'Adress-ID', art: 'ro-tech' },
  { key: 'personId', label: 'Kunden-ID', art: 'ro-tech' },
];

export const KONTAKTKANAL_FELDER: FeldDef[] = [
  { key: 'kanal', label: 'Kanal', art: 'select', optionen: KONTAKT_KANAELE },
  { key: 'wertRoh', label: 'Wert', art: 'text' },
  { key: 'wertNorm', label: 'Normform', art: 'ro' },
  { key: 'istPrimaer', label: 'Hauptkanal', art: 'bool' },
  { key: 'verifiziert', label: 'Verifiziert', art: 'bool' },
  { key: 'quelle', label: 'Quelle', art: 'text' },
  { key: 'erstelltAt', label: 'Angelegt', art: 'ro-zeit' },
  { key: 'geaendertAt', label: 'Geändert', art: 'ro-zeit' },
  { key: 'id', label: 'Kanal-ID', art: 'ro-tech' },
  { key: 'personId', label: 'Kunden-ID', art: 'ro-tech' },
];

export const EINWILLIGUNG_FELDER: FeldDef[] = [
  { key: 'kanal', label: 'Kanal', art: 'select', optionen: EINWILLIGUNG_KANAELE },
  { key: 'status', label: 'Status', art: 'select', optionen: EINWILLIGUNG_STATUS },
  { key: 'doubleOptIn', label: 'Double-Opt-in', art: 'bool3' },
  { key: 'zeitpunkt', label: 'Zeitpunkt', art: 'zeit' },
  { key: 'quelleNachweis', label: 'Nachweis / Quelle', art: 'text' },
  { key: 'widerrufAm', label: 'Widerrufen am', art: 'zeit' },
  { key: 'erstelltAt', label: 'Angelegt', art: 'ro-zeit' },
  { key: 'geaendertAt', label: 'Geändert', art: 'ro-zeit' },
  { key: 'id', label: 'Einwilligungs-ID', art: 'ro-tech' },
  { key: 'personId', label: 'Kunden-ID', art: 'ro-tech' },
];

/** #851 „Kunde seit“ NULL: NEO liefert kein Anlagedatum (Tooltip in Liste) */
export const KUNDE_SEIT_UNBEKANNT = 'NEO liefert kein Anlagedatum';

/** #851 Anzeige einer Filiale: „2 · Alter Peter“ (inaktiv markiert) */
export const filialeLabel = (f: CrmFiliale): string => `${f.kuerzel} · ${f.name}${f.aktiv ? '' : ' (inaktiv)'}`;

/** #851 Ladezustand der Filialliste (GET /api/crm/v1/filialen) */
export type FilialZustand = { kind: 'laden' } | { kind: 'fehler' } | { kind: 'ok'; filialen: readonly CrmFiliale[] };

/**
 * #851 Stammfiliale IMMER als Auswahl aus kunden.filiale (nie Freitext): wählbar sind aktive Filialen; ein bereits
 * gespeicherter, nicht mehr aktiver/unbekannter Wert bleibt als Option sichtbar (sonst würde das Formular ihn still
 * leeren). Während des Ladens und nach einem Ladefehler ist die Auswahl gesperrt, der bestehende Wert bleibt erhalten
 * (kein PATCH), dazu Statuszeile und — bei Fehler — „Erneut laden“.
 */
export function mitFilialen(defs: readonly FeldDef[], zustand: FilialZustand, aktuell?: string | null, erneut?: () => void): FeldDef[] {
  const labels: Record<string, string> = zustand.kind === 'ok' ? Object.fromEntries(zustand.filialen.map((f) => [f.kuerzel, filialeLabel(f)])) : {};
  const optionen = zustand.kind === 'ok' ? zustand.filialen.filter((f) => f.aktiv).map((f) => f.kuerzel) : [];
  if (aktuell && !optionen.includes(aktuell)) {
    optionen.push(aktuell);
    labels[aktuell] ??= zustand.kind === 'ok' ? `${aktuell} (unbekannte Filiale)` : aktuell;
  }
  const extra: Partial<FeldDef> =
    zustand.kind === 'laden'
      ? { gesperrt: true, status: { text: 'Filialen werden geladen …' } }
      : zustand.kind === 'fehler'
        ? { gesperrt: true, status: { text: 'Filialen konnten nicht geladen werden.', fehler: true, ...(erneut ? { erneut } : {}) } }
        : {};
  return defs.map((d) => (d.key === 'stammfiliale' ? { ...d, art: 'select', optionen, optionLabels: labels, ...extra } : d));
}

/** Beim Ändern feste Felder (API lehnt sie ab) */
export const FEST_BEIM_AENDERN: Record<'person' | 'adresse' | 'kontaktkanal' | 'einwilligung', readonly string[]> = {
  person: ['quelle', 'quellenId'],
  adresse: [],
  kontaktkanal: [],
  einwilligung: ['kanal'],
};

export const istNurLesen = (d: FeldDef): boolean => d.art === 'ro' || d.art === 'ro-zeit' || d.art === 'ro-tech';

/* ---------------------------------------------------------- Zeit: Europe/Berlin ↔ UTC ---------------------------------------------------------- */

const ZONE = 'Europe/Berlin';
const DATUM = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeZone: ZONE });
const ZEIT = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short', timeZone: ZONE });
const TEILE = new Intl.DateTimeFormat('en-US', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
const ISO_DATUM = /^\d{4}-\d{2}-\d{2}$/;
const ISO_ZEIT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

export const LEER = '—';

/** Wandzeit in Europe/Berlin als Zahlen */
function berlinTeile(d: Date): { y: number; mo: number; d: number; h: number; mi: number; s: number } {
  const t: Record<string, number> = {};
  for (const p of TEILE.formatToParts(d)) if (p.type !== 'literal') t[p.type] = Number(p.value);
  return { y: t.year!, mo: t.month!, d: t.day!, h: t.hour!, mi: t.minute!, s: t.second! };
}

/** ISO (UTC) → datetime-local-Wert in Europe/Berlin (Minuten; Sekunden gehören nicht ins Eingabefeld) */
export function zuLokal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const t = berlinTeile(d);
  const z = (n: number) => String(n).padStart(2, '0');
  return `${t.y}-${z(t.mo)}-${z(t.d)}T${z(t.h)}:${z(t.mi)}`;
}

/** datetime-local-Wert (Wandzeit Europe/Berlin, unabhängig von der Browser-Zeitzone) → ISO UTC */
export function ausLokal(lokal: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(lokal.trim());
  if (!m) return null;
  const [y, mo, d, h, mi, s] = [m[1], m[2], m[3], m[4], m[5], m[6] ?? '0'].map(Number) as [number, number, number, number, number, number];
  // Annahme UTC, dann um den Berliner Versatz korrigieren; zweiter Durchlauf fängt den Versatzwechsel (Sommer/Winter) ab
  let utc = Date.UTC(y, mo - 1, d, h, mi, s);
  for (let i = 0; i < 2; i++) {
    const t = berlinTeile(new Date(utc));
    const wand = Date.UTC(t.y, t.mo - 1, t.d, t.h, t.mi, t.s);
    const soll = Date.UTC(y, mo - 1, d, h, mi, s);
    if (wand === soll) break;
    utc += soll - wand;
  }
  return new Date(utc).toISOString();
}

export function formatZeit(iso: string | null | undefined): string {
  if (!iso) return LEER;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : ZEIT.format(d);
}
export function formatDatum(iso: string | null | undefined): string {
  if (!iso) return LEER;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? String(iso) : DATUM.format(d);
}

/** Anzeige eines Werts (API-Wert oder jsonb aus dem Änderungsprotokoll): lesbar, ohne Anführungszeichen, null → „—“ */
export function wertText(v: unknown, art?: FeldArt): string {
  if (v === null || v === undefined || v === '') return LEER;
  if (typeof v === 'boolean') return v ? 'ja' : 'nein';
  if (typeof v === 'number') return new Intl.NumberFormat('de-DE').format(v);
  if (Array.isArray(v)) return v.length ? v.map((x) => wertText(x)).join(', ') : LEER;
  if (typeof v === 'string') {
    if (art === 'datum' || ISO_DATUM.test(v)) return formatDatum(v);
    if (art === 'zeit' || art === 'ro-zeit' || ISO_ZEIT.test(v)) return formatZeit(v);
    if (art === 'dezimal' && /^-?\d+(\.\d+)?$/.test(v)) return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v));
    return v;
  }
  return JSON.stringify(v);
}

/** Anzeige eines Feldwerts mit deutscher Beschriftung der CHECK-Werte (Rohwert bleibt in title/Formular) */
export function feldText(feldKey: string, v: unknown, art?: FeldArt): string {
  if (typeof v === 'string' && WERT_LABELS[zuFeldKey(feldKey)]) return wertLabel(feldKey, v);
  return wertText(v, art);
}

/** Ist der Wert eine ganze Zeile (insert/delete) — dann einklappbar darstellen */
export function istZeile(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Spaltenname (snake_case aus dem Protokoll) → Beschriftung aus dem Feldkatalog, sonst der Name selbst */
const LABELS = new Map<string, string>();
for (const d of [...PERSON_GRUPPEN.flatMap((g) => g.felder), ...ADRESSE_FELDER, ...KONTAKTKANAL_FELDER, ...EINWILLIGUNG_FELDER]) {
  LABELS.set(d.key, d.label);
  LABELS.set(d.key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`), d.label);
}
LABELS.set('quellen_id', 'Quellen-ID');
LABELS.set('fremd_id', 'Fremd-ID');
LABELS.set('fremd_nr2', 'Zweite Nummer');
LABELS.set('zuletzt_gesehen', 'Zuletzt gesehen');
LABELS.set('im_quellsystem_geloescht', 'Im Quellsystem gelöscht');
LABELS.set('rohdaten', 'Rohdaten');
LABELS.set('system', 'System');
export function feldLabel(feld: string | null): string {
  if (!feld) return LEER;
  return LABELS.get(feld) ?? feld;
}

/** „Anzeigename (principal-id)“ aus geaendert_von trennen; Agent/Workflow/Import ohne Klammer bleiben als Name */
export function akteurTeile(geaendertVon: string): { name: string; id: string | null } {
  const m = /^(.*\S)\s\(([^()]+)\)$/.exec(geaendertVon);
  return m ? { name: m[1]!, id: m[2]! } : { name: geaendertVon, id: null };
}
export const kurzId = (id: string): string => (id.length > 12 ? `${id.slice(0, 8)}…` : id);

export const TABELLE_LABEL: Record<string, string> = {
  person: 'Person',
  adresse: 'Adresse',
  kontaktkanal: 'Kontaktkanal',
  einwilligung: 'Einwilligung',
  quelle_ref: 'Quelle',
  ereignis: 'Ereignis',
};
export const AKTION_LABEL: Record<string, string> = { insert: 'angelegt', update: 'geändert', delete: 'gelöscht', import: 'importiert', merge: 'zusammengeführt' };
export const AKTEUR_LABEL: Record<string, string> = { mitarbeiter: 'Mitarbeiter', agent: 'Agent', workflow: 'Workflow', import: 'Import', system: 'System' };
export const QUELLE_LABEL: Record<CrmPerson['quelle'], string> = { shopify: 'Shop', neo: 'NEO', portal: 'Portal' };

/** #846 Quelle für Liste/Datensatz: „Shop + NEO“, wenn die Person Verweise in beiden Systemen hat; sonst Herkunft */
export function quelleAnzeige(k: { quelle: CrmPerson['quelle']; quellenSysteme?: readonly string[] }): string {
  const s = k.quellenSysteme ?? [];
  if (s.includes('shopify') && s.includes('neo')) return 'Shop + NEO';
  if (s.includes('neo') && !s.includes('shopify')) return 'NEO';
  return QUELLE_LABEL[k.quelle];
}

/** #846 Abgleich-Stufen (kunden.abgleich.stufe) */
export const ABGLEICH_STUFE_LABEL: Record<number, string> = {
  1: 'NEO-Nummer am Shop-Konto', 2: 'E-Mail', 3: 'Telefon', 4: 'Name + PLZ + Straße', 5: 'Name + Geburtsdatum', 6: 'ähnlicher Name, gleiche PLZ',
};
export const ABGLEICH_WIRKUNG_LABEL: Record<string, string> = { zusammengefuehrt: 'zusammengeführt', pruefen: 'prüfen', verdacht: 'Verdacht', haushalt: 'gleicher Haushalt' };

/* ---------------------------------------------------------- Formular ↔ API ---------------------------------------------------------- */

/** API-Wert → Formularwert (immer String; bool → 'true'/'false'; bool3 null → '') */
export function zuFormular(def: FeldDef, v: unknown): string {
  if (v === null || v === undefined) return def.art === 'bool' ? 'false' : '';
  switch (def.art) {
    case 'bool':
    case 'bool3':
      return v ? 'true' : 'false';
    case 'tags':
      return Array.isArray(v) ? v.join(', ') : '';
    case 'zeit':
      return typeof v === 'string' ? zuLokal(v) : '';
    default:
      return String(v);
  }
}

/** Formularwert → API-Wert ('' → null; Zeit als Wandzeit Europe/Berlin) */
export function ausFormular(def: FeldDef, s: string): unknown {
  const t = s.trim();
  switch (def.art) {
    case 'bool':
      return t === 'true';
    case 'bool3':
      return t === '' ? null : t === 'true';
    case 'tags':
      return t ? t.split(',').map((x) => x.trim()).filter(Boolean) : [];
    case 'int':
      return t ? Number(t) : null;
    case 'dezimal':
      return t ? t.replace(',', '.') : null;
    case 'zeit':
      return t ? ausLokal(t) : null;
    default:
      return t ? t : null;
  }
}

/**
 * Nur im Formular tatsächlich geänderte, schreibbare Felder als PATCH-Körper. Vergleich auf Formularebene
 * (Ausgangswert → Formularstring vs. aktueller Formularstring), damit unberührte Felder — insbesondere Zeitpunkte mit
 * Sekunden — nie mitgeschickt werden. Ohne Änderung: leeres Objekt (kein Request).
 */
export function patchAus(defs: readonly FeldDef[], original: Record<string, unknown>, formular: Record<string, string>, fest: readonly string[] = []): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const d of defs) {
    if (istNurLesen(d) || fest.includes(d.key)) continue;
    const vorher = zuFormular(d, original[d.key]);
    const jetzt = formular[d.key] ?? vorher;
    if (d.art === 'tags' ? JSON.stringify(ausFormular(d, jetzt)) === JSON.stringify(ausFormular(d, vorher)) : jetzt.trim() === vorher.trim()) continue;
    patch[d.key] = ausFormular(d, jetzt);
  }
  return patch;
}

/** Leerer Datensatz fürs Anlegen */
export function leer(defs: readonly FeldDef[]): Record<string, string> {
  return Object.fromEntries(defs.map((d) => [d.key, d.art === 'bool' ? 'false' : '']));
}
